import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import mermaid from "mermaid";
import { initMermaidStatic } from "../../../utils/mermaid-init";

// Initialize mermaid with static colors
initMermaidStatic();

type DiagramType = "architecture" | "deploy" | "flow";

interface MermaidDiagramProps {
  /** Custom Mermaid diagram string. When provided, renders this instead of the static diagrams. */
  diagram?: string;
  /** When diagram prop is not provided, renders one of the static built-in diagrams. */
  type?: DiagramType;
  /** When true, hides the toggle buttons for switching between static diagrams. */
  hideToggle?: boolean;
}

const DIAGRAMS: Record<DiagramType, string> = {
  architecture: `flowchart TB
    subgraph Cloud["AWS Cloud"]
      subgraph VPC["VPC: 10.0.0.0/16"]
        subgraph Public["Public Subnets"]
          LB["Load Balancer"]
        end
        subgraph Private["Private Subnets"]
          API1["API Service"]
          API2["API Service"]
          API3["API Service"]
        end
        DB["Database<br/>(PostgreSQL)"]
      end
      IG["Internet<br/>Gateway"]
    end
    
    Client["Client<br/>Requests"] --> LB
    LB --> API1
    LB --> API2
    LB --> API3
    API1 --> DB
    API2 --> DB
    API3 --> DB
    IG --> LB
    
    style Cloud fill:#f0f9ff,stroke:#0066ff,stroke-width:2px
    style VPC fill:#e0e7ff,stroke:#0066ff,stroke-width:1px
    style Public fill:#dcfce7,stroke:#10b981,stroke-width:1px
    style Private fill:#fef3c7,stroke:#f59e0b,stroke-width:1px
    style LB fill:#0066ff,stroke:#0066ff
    style API1 fill:#0066ff,stroke:#0066ff
    style API2 fill:#0066ff,stroke:#0066ff
    style API3 fill:#0066ff,stroke:#0066ff
    style DB fill:#7c3aed,stroke:#7c3aed
    style IG fill:#10b981,stroke:#10b981
    style Client fill:#374151,stroke:#374151`,

  deploy: `sequenceDiagram
    participant U as User
    participant W as Wizard
    participant A as API
    participant G as GitHub
    participant K as K8s
    
    U->>W: Select Template
    W->>A: Create System
    A->>G: Provision Repo
    G-->>A: Repo Created
    A->>K: Deploy Manifest
    K-->>A: Pods Running
    A-->>U: Live URL`,

  flow: `flowchart LR
    subgraph Build["Build Phase"]
      Git["Git Push"] --> CI["CI Pipeline"]
      CI --> B["Docker Build"]
      B --> Push["Push to GHCR"]
    end
    
    subgraph Deploy["Deploy Phase"]
      Push --> Webhook["Webhook"]
      Webhook --> Orch["Orchestrator"]
      Orch --> Render["Render Manifest"]
      Render --> Apply["Apply to K8s"]
    end
    
    subgraph Runtime["Runtime"]
      Apply --> Pods["Pods Running"]
      Pods --> LB["Load Balancer"]
      LB --> Users["End Users"]
    end
    
    style Build fill:#dbeafe,stroke:#2563eb
    style Deploy fill:#dcfce7,stroke:#16a34a
    style Runtime fill:#fef3c7,stroke:#d97706`,
};

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  diagram,
  type = "architecture",
  hideToggle = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [diagramType, setDiagramType] = useState<DiagramType>(type);
  const [rendered, setRendered] = useState(false);

  // When a custom diagram string is provided, use it directly.
  // Otherwise fall back to the static built-in diagrams.
  const diagramSource = diagram ?? DIAGRAMS[diagramType];

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;

      try {
        const id = `mermaid-${diagram ? "custom" : diagramType}-${Date.now()}`;
        const { svg } = await mermaid.render(id, diagramSource);
        setSvg(svg);
        setRendered(true);
      } catch {
        // Mermaid rendering failed
        setRendered(false);
      }
    };

    renderDiagram();
  }, [diagramSource, diagram, diagramType]);

  const handleTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: DiagramType | null,
  ) => {
    if (newType !== null) {
      setDiagramType(newType);
      setRendered(false);
    }
  };

  return (
    <Box>
      {/* Diagram type selector — only for static built-in diagrams */}
      {!diagram && !hideToggle && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, gap: 1 }}>
          <ToggleButtonGroup
            value={diagramType}
            exclusive
            onChange={handleTypeChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontSize: "0.8rem",
                px: 2,
              },
            }}
          >
            <ToggleButton value="architecture">Architecture</ToggleButton>
            <ToggleButton value="deploy">Deploy Flow</ToggleButton>
            <ToggleButton value="flow">Pipeline</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Mermaid diagram */}
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: rendered ? 1 : 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          "& svg": {
            maxWidth: "100%",
            height: "auto",
          },
        }}
        ref={containerRef}
      >
        {svg && (
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
      </MotionBox>

      {!rendered && (
        <Box
          sx={{
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">Loading diagram...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MermaidDiagram;
